import React, { useState, useRef, useCallback } from "react"
import { UploadCloud, CheckCircle2, XCircle, ChevronDown, ChevronRight } from "lucide-react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "src/components/ui/dialog"
import { Button } from "src/components/ui/button"
import { useImportLeads } from "../hooks/useLeads"
import { useLeadStatuses } from "../hooks/useLeadStatuses"
import { useToast } from "src/components/ui/toast"
import { cn } from "src/utils/cn"

// Column header normalisation map
const COLUMN_MAP = {
  name: "name", phone: "phone", mobile: "phone",
  email: "email", status: "status", source: "source",
  notes: "notes", remark: "notes",
}

const VALID_SOURCES  = ["channel_partner", "direct", "referral", "campaign", "walk_in"]
const INDIAN_PHONE   = /^[6-9]\d{9}$/

function normaliseHeader(h) {
  return COLUMN_MAP[h.toLowerCase().trim()] ?? h.toLowerCase().trim()
}

function validateRow(row, idx, validStatuses) {
  const errors = []
  if (!row.name?.trim()) errors.push("Name is required")
  if (row.phone && !INDIAN_PHONE.test(row.phone)) errors.push("Invalid Indian phone number")
  if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) errors.push("Invalid email")
  if (!row.phone && !row.email) errors.push("At least one of phone or email is required")
  if (row.status && validStatuses.length > 0 && !validStatuses.includes(row.status)) errors.push(`Unknown status: ${row.status}`)
  if (row.source && !VALID_SOURCES.includes(row.source)) errors.push(`Unknown source: ${row.source}`)
  return errors
}

function downloadSampleCsv() {
  const rows = [
    ["name", "phone", "email", "status", "source"],
    ["Amit Sharma", "9876543210", "amit@example.com", "new", "direct"],
    ["Priya Mehta", "9123456789", "", "contacted", "referral"],
  ]
  const csv  = rows.map((r) => r.join(",")).join("\n")
  const blob = new Blob([csv], { type: "text/csv" })
  const url  = URL.createObjectURL(blob)
  const a    = Object.assign(document.createElement("a"), { href: url, download: "leads_template.csv" })
  a.click()
  URL.revokeObjectURL(url)
}

// ── Step 1: Upload ────────────────────────────────────────────────────────────
function UploadStep({ onFile, onClose }) {
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef(null)

  const handleFiles = (files) => {
    const file = files?.[0]
    if (!file) return
    if (file.size > 2 * 1024 * 1024) { alert("File must be under 2 MB"); return }
    onFile(file)
  }

  return (
    <div className="flex flex-col gap-5">
      <div
        className={cn(
          "flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-14 cursor-pointer transition-colors",
          dragging ? "border-slate-400 bg-slate-50" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
        )}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files) }}
      >
        <UploadCloud className="h-10 w-10 text-slate-300" />
        <div className="text-center">
          <p className="text-sm font-medium text-slate-700">Drag & drop your file here</p>
          <p className="text-xs text-slate-400">or click to browse</p>
        </div>
        <p className="text-[11px] text-slate-400">Supports .csv · Max 2 MB · Max 500 rows</p>
        <input
          ref={inputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      <button
        onClick={downloadSampleCsv}
        className="text-xs text-slate-500 underline underline-offset-2 hover:text-slate-700 text-left"
      >
        📄 Download sample CSV template
      </button>

      <div className="flex justify-end">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
      </div>
    </div>
  )
}

// ── Step 2: Preview ───────────────────────────────────────────────────────────
function PreviewStep({ rows, onBack, onImport, isPending }) {
  const validRows   = rows.filter((r) => r.errors.length === 0)
  const invalidRows = rows.filter((r) => r.errors.length > 0)
  const preview     = rows.slice(0, 10)

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3 text-sm">
        <span className="text-slate-600">{rows.length} rows found</span>
        <span className="text-green-600 font-medium">✓ {validRows.length} valid</span>
        {invalidRows.length > 0 && (
          <span className="text-red-500 font-medium">⚠ {invalidRows.length} errors</span>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {["Row", "Name", "Phone", "Status", "Source", "Errors"].map((h) => (
                <th key={h} className="px-3 py-2 text-left font-medium text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {preview.map((row, i) => (
              <tr key={i} className={cn("border-b border-slate-100", row.errors.length > 0 && "bg-red-50")}>
                <td className="px-3 py-2 text-slate-400">{i + 1}</td>
                <td className="px-3 py-2 text-slate-700 max-w-[100px] truncate">{row.name || "—"}</td>
                <td className="px-3 py-2 text-slate-600">{row.phone || "—"}</td>
                <td className="px-3 py-2 text-slate-600">{row.status || "new"}</td>
                <td className="px-3 py-2 text-slate-600">{row.source || "—"}</td>
                <td className="px-3 py-2">
                  {row.errors.length > 0
                    ? <span className="text-red-500">⚠ {row.errors[0]}</span>
                    : <span className="text-slate-300">—</span>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 10 && (
          <p className="px-3 py-2 text-[11px] text-slate-400 border-t border-slate-100">
            Showing first 10 of {rows.length} rows
          </p>
        )}
      </div>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} disabled={isPending}>← Back</Button>
        <Button
          onClick={() => onImport(validRows)}
          disabled={validRows.length === 0 || isPending}
        >
          {isPending ? "Importing…" : `Import ${validRows.length} valid rows →`}
        </Button>
      </div>
    </div>
  )
}

// ── Step 3: Result ────────────────────────────────────────────────────────────
function ResultStep({ result, error, onClose, onReset }) {
  const [openRow, setOpenRow] = useState(null)

  if (error) {
    return (
      <div className="flex flex-col items-center gap-4 py-6 text-center">
        <XCircle className="h-12 w-12 text-red-400" />
        <div>
          <p className="font-semibold text-slate-800">Import Failed</p>
          <p className="text-sm text-slate-500 mt-1">Something went wrong. Please try again.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onReset}>← Try Again</Button>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="h-8 w-8 text-green-500 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-slate-800">Import Complete</p>
          <p className="text-sm text-slate-600 mt-0.5">
            <span className="text-green-600 font-medium">{result.imported} leads</span> imported successfully.
            {result.skipped > 0 && (
              <> <span className="text-slate-400">{result.skipped} rows skipped.</span></>
            )}
          </p>
        </div>
      </div>

      {result.errors?.length > 0 && (
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <p className="px-3 py-2 text-xs font-medium text-slate-500 bg-slate-50 border-b border-slate-200">
            Row errors
          </p>
          {result.errors.map((e) => (
            <div key={e.row} className="border-b border-slate-100 last:border-0">
              <button
                className="flex w-full items-center justify-between px-3 py-2 text-xs hover:bg-slate-50"
                onClick={() => setOpenRow(openRow === e.row ? null : e.row)}
              >
                <span className="text-slate-600">Row {e.row}</span>
                {openRow === e.row ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              </button>
              {openRow === e.row && (
                <ul className="px-4 pb-2 text-[11px] text-red-500 space-y-0.5">
                  {e.messages.map((m, i) => <li key={i}>• {m}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={onClose}>Close</Button>
      </div>
    </div>
  )
}

// ── Main modal ────────────────────────────────────────────────────────────────
export function ImportLeadsModal({ open, onClose, onSuccess }) {
  const [step, setStep]       = useState(1)
  const [parsedRows, setParsedRows] = useState([])
  const { toast } = useToast()
  const { statuses: leadStatuses } = useLeadStatuses()
  const validStatusValues = leadStatuses.map((s) => s.value)
  const { mutate, isPending, isSuccess, isError, result, error, reset } = useImportLeads()

  function handleClose() {
    setStep(1)
    setParsedRows([])
    reset()
    onClose()
  }

  function handleReset() {
    setStep(1)
    setParsedRows([])
    reset()
  }

  // Parse CSV file (no papaparse — use built-in split)
  async function handleFile(file) {
    const text = await file.text()
    const lines = text.trim().split("\n").filter(Boolean)
    if (lines.length < 2) return

    const rawHeaders = lines[0].split(",").map(normaliseHeader)
    const rows = lines.slice(1).map((line, idx) => {
      const cells = line.split(",")
      const row = {}
      rawHeaders.forEach((h, i) => { row[h] = cells[i]?.trim() || "" })
      row.errors = validateRow(row, idx + 1, validStatusValues)
      return row
    })

    if (rows.length > 500) { alert("Maximum 500 rows allowed"); return }
    setParsedRows(rows)
    setStep(2)
  }

  async function handleImport(validRows) {
    const payload = validRows.map(({ errors: _e, ...rest }) => rest)
    try {
      const data = await mutate(payload)
      setStep(3)
      toast({ title: `${data.imported} leads imported`, description: data.skipped > 0 ? `${data.skipped} rows skipped` : undefined })
      onSuccess?.()
    } catch {
      setStep(3)
    }
  }

  const TITLES = { 1: "Import Leads", 2: "Preview & Validate", 3: "Import Result" }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{TITLES[step]}</DialogTitle>
          {step === 1 && (
            <DialogDescription>Upload a CSV file to bulk import leads.</DialogDescription>
          )}
        </DialogHeader>

        {step === 1 && <UploadStep onFile={handleFile} onClose={handleClose} />}
        {step === 2 && (
          <PreviewStep
            rows={parsedRows}
            onBack={() => setStep(1)}
            onImport={handleImport}
            isPending={isPending}
          />
        )}
        {step === 3 && (
          <ResultStep
            result={result}
            error={isError ? error : null}
            onClose={handleClose}
            onReset={handleReset}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
